import { getAntiforgeryToken } from "../api/http";
import type {
  TagCatalogContext,
  TagCatalogPage,
  TagCatalogQuery,
  TagChangeResult,
  TagDefinition,
  TagEdit,
  TagExtract,
  TagStateChange,
} from "../api/generated/model";

// Cliente del catálogo de etiquetas, con las mismas reglas que ContactTagCatalogClient del portal actual: antes de cada
// pedido comprueba que la empresa, la persona y la sesión siguen siendo las mismas y que el permiso alcanza; después,
// que el contexto no cambió en el medio. El servidor vuelve a decidir todo; esto solo evita mostrar datos de otra sesión.

export class ContactRequestFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

export class TagCatalogClient {
  currentSessionStamp: string | null = null;
  constructor(
    readonly tenant: number,
    readonly user: number,
  ) {}

  list(query: TagCatalogQuery, signal?: AbortSignal): Promise<TagCatalogPage> {
    return this.send("query", query, signal);
  }
  save(id: number | null, edit: TagEdit): Promise<TagDefinition> {
    return this.send(id ? `${id}/save` : "create", edit);
  }
  state(changes: TagStateChange[]): Promise<TagChangeResult[]> {
    return this.send("state", changes);
  }
  async extract(extract: TagExtract): Promise<string> {
    const response = await this.request("extract", extract);
    return (await response.text()).replace(/^﻿/, "");
  }

  private async send<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const response = await this.request(path, body, signal);
    return (await response.json()) as T;
  }

  private async request(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
    const expected = this.currentSessionStamp;
    const before = await this.context(signal);
    if (expected !== null && expected !== before.sessionStamp) throw new ContactRequestFailure(409, "SESSION_INVALID");
    const writes = path === "create" || path === "state" || path.endsWith("/save");
    if (!before.access.canReadTags || (writes && !before.access.canManageTags))
      throw new ContactRequestFailure(403, "CRM_FORBIDDEN");
    const response = await fetch(`/api/portal/contacts/catalog/${path}`, {
      method: "POST",
      credentials: "same-origin",
      signal,
      headers: {
        "Content-Type": "application/json",
        "X-Bheli-Contact-Tenant": String(this.tenant),
        "X-CSRF-TOKEN": getAntiforgeryToken() ?? "",
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const failure = await response.json().catch(() => null);
      throw new ContactRequestFailure(response.status, failure?.code ?? "DEPENDENCY_UNAVAILABLE");
    }
    const after = await this.context(signal);
    if (
      before.sessionStamp !== after.sessionStamp ||
      JSON.stringify(before.access) !== JSON.stringify(after.access) ||
      before.scopeRevision !== after.scopeRevision
    )
      throw new ContactRequestFailure(409, "CONTACT_SCOPE_CHANGED");
    return response;
  }

  private async context(signal?: AbortSignal): Promise<TagCatalogContext> {
    const response = await fetch("/api/portal/contacts/catalog/context", { credentials: "same-origin", signal });
    if (!response.ok) throw new ContactRequestFailure(response.status, "SESSION_INVALID");
    const context = (await response.json()) as TagCatalogContext;
    if (context.organizationId !== this.tenant || context.userId !== this.user)
      throw new ContactRequestFailure(409, "TENANT_CHANGED");
    this.currentSessionStamp = context.sessionStamp;
    return context;
  }
}
